// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import SignInButton from "@werner/App/Components/Common/SignInButton";
import Notify from "@werner/App/Components/Notify/Notify";
import getSupplierAvailability, { XWarehouseModel } from "@werner/App/Handlers/ProductDetails/GetSupplierAvailability";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import Modal, { ModalPresentationProps } from "@werner/MobiusOverrides/Modal/Modal";
import Tooltip from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { hideElementOnPrint, isSiteMessageEmpty, mergeWithCss, parseBoolean } from "@werner/XDL/Utilities";
import color from "@werner/XDL/Utilities/color";
import parse from "html-react-parser";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Changes
    pickupMessage?: string;
    hideLink?: boolean;
    linkOnly?: boolean;
    showFaildInventoryMessage?: boolean;
    shouldShowErrorMessage?: boolean;
    hideOutOfStockMessage?: boolean;
    leadTimeMessage?: string;
}
export type XNGProductAvailabilityProps = OwnProps;

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Changes
    product: getProductState(state, ownProps.productId).value,
    isAuthenticated: xIsAuthenticated(state),
    settingsBrandName: getSettingsCollection(state).customSettings.brandName,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    getSupplierAvailability,
    // XNG-Changes
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Changes
    warehouseMessageText?: TypographyPresentationProps;
    dash?: TypographyPresentationProps;
    loginMessageWrapper?: InjectableCss;
    loginNotify?: TypographyPresentationProps;
    viewSimilarProductsLink?: LinkPresentationProps;
    leadTimeTextStyle?: TypographyPresentationProps;
    innerWrapper?: InjectableCss;
    supplierInventory?: InjectableCss;
    customOutOfStockMessageStyle?: TypographyPresentationProps;
}

// XNG-Changes
export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 5px;
            ${({ isProductDetailsPage }: any) => isProductDetailsPage && "margin-top: 12px; flex-wrap:wrap;"}
            & .availability-link-wrapper {
                display: flex;
                gap: 5px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
    messageGridItem: { width: 5 },
    availabilityByWarehouseLinkGridItem: {
        width: 7,
        css: css``,
    },
    availabilityByWarehouseTableHead: {
        css: css`
            border-bottom: 3px solid ${color("common.ui1", 0.75)};
            border-top: unset;
        `,
    },
    availabilityByWarehouseModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalContent: css`
                padding: 30px;
            `,
            modalTitle: css`
                overflow-y: auto;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        size: 14,
        weight: 400,
        css: css`
            width: 100%;
            letter-spacing: 0;
            line-height: 22px;
            white-space: nowrap;
            ${wrapInContainerStyles}
            & > b {
                font-weight: 500;
            }
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        size: 12,
        weight: "bold",
        color: "danger",
    },
    availabilityByWarehouseLink: {
        color: "common.ui1",
        typographyProps: {
            size: 14,
            weight: 400,
            css: css`
                width: 100%;
                letter-spacing: 0;
                line-height: 22px;
                ${wrapInContainerStyles}
            `,
        },
    },
    viewSimilarProductsLink: {
        color: "common.ui1",
        typographyProps: {
            size: 14,
            weight: 400,
            css: css`
                width: 100%;
                letter-spacing: 0;
                line-height: 22px;
                margin-top: 8px;
                ${wrapInContainerStyles}
            `,
        },
    },
    warehouseMessageText: {
        size: 14,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 22px;
        `,
    },
    dash: {
        color: "common.ui1",
        css: css`
            line-height: 20px;
            ${hideElementOnPrint};
        `,
    },
    loginMessageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            ${({ isProductDetailsPage }: any) => isProductDetailsPage && "margin-top: 12px;"}
        `,
    },
    loginNotify: {
        css: css`
            min-width: unset;
        `,
    },
    errorWrapper: {
        css: css`
            ${({ isProductDetailsPage }: any) => isProductDetailsPage && "margin-top: 12px;"}
        `,
    },
    availabilityByWarehouseTable: {
        cssOverrides: {
            wrapper: css`
                overflow-x: unset;
                overflow-y: unset;
            `,
        } as any,
    },
    innerWrapper: {
        css: css`
            flex-direction: column;
        `,
    },
    leadTimeTextStyle: {
        css: css`
            min-width: 100%;
        `,
    },
    supplierInventory: {
        css: css`
            width: 100%;
        `,
    },
    customOutOfStockMessageStyle: {
        css: css`
            min-width: 100%;
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().string()};
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    // XNG-Changes
    hideLink = false,
): boolean => {
    // XNG-Changes: check for hideLink
    if (hideLink) {
        return false;
    }
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const similarProductsSelector = "div[data-carousel-type='SimilarProduct']";

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    linkOnly,
    hideLink,
    pickupMessage,
    product,
    isAuthenticated,
    showFaildInventoryMessage = true,
    shouldShowErrorMessage = true,
    getSupplierAvailability,
    hideOutOfStockMessage = false,
    leadTimeMessage,
    // XNG-Changes
    loadProductInfoList,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [supplierModalOpen, setSupplierModalOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [supplierWarehouses, setSupplierWarehouses] = useState<XWarehouseModel[] | undefined>(undefined);

    const [errorMessage, setErrorMessage] = useState("");
    const [supplierErrorMessage, setSupplierErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [shouldShowViewSimilarProducts, setShouldShowViewSimilarProducts] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setSupplierModalOpen(false);
        setErrorMessage("");
        setSupplierErrorMessage("");
    };

    useEffect(() => {
        if (isProductDetailsPage && !shouldShowViewSimilarProducts) {
            const elem = document.querySelector(similarProductsSelector);
            setShouldShowViewSimilarProducts(!!isProductDetailsPage && !!elem);
        }
    });

    // XNG-Changes
    useEffect(() => {
        if (!product) {
            loadProductInfoList({
                id: "productAvailability",
                getProductCollectionParameter: { productIds: [productId] },
            });
        }
    }, [product]);

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    // <XNG-Changes>
    const hasSupplierInventory =
        isProductDetailsPage && product && parseBoolean(product.properties.hasInventoryService);
    const scrollToSimilarProducts = () => {
        const elem = document.querySelector(similarProductsSelector);
        if (!elem) {
            return;
        }
        elem.scrollIntoView({
            behavior: "smooth",
        });
    };
    const openSupplierAvailabilityModal = () => {
        setIsLoading(true);
        setSupplierModalOpen(true);
        getSupplierAvailability({
            productNumber: product?.productNumber || "",
            onSuccess: result => {
                setSupplierWarehouses(result.warehouses);
                setSupplierErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };
    // </XNG-Changes>

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);
    // XNG-changes: use mergeWithCss insted of mergeToNew
    const [styles] = useState(() => mergeWithCss(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }

    const shouldHideElementOnError =
        (!isAuthenticated &&
            product &&
            product.properties.unauthenticatedGuestCustomization?.toLowerCase() === "true" &&
            !shouldShowErrorMessage) ||
        (failedToLoadInventory && !shouldShowErrorMessage);

    if (shouldHideElementOnError) {
        return null;
    }
    // <XNG-Changes>
    if (!isAuthenticated && product && product.properties.unauthenticatedGuestCustomization?.toLowerCase() === "true") {
        return (
            <StyledWrapper {...styles.loginMessageWrapper} {...{ isProductDetailsPage }}>
                <Notify showClose={false} type="warning" {...styles.loginNotify}>
                    {siteMessage("Werner_Inventory_UnauthenticatedCategoryMessage")}
                </Notify>
                <SignInButton type="link" labelOverride={translate("Login for Inventory")} />
            </StyledWrapper>
        );
    }
    // </XNG-Changes>

    if (failedToLoadInventory && showFaildInventoryMessage) {
        return (
            <StyledWrapper {...styles.errorWrapper} {...{ isProductDetailsPage }}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    // XNG-Change -- WES-1889
    const hideAvailablity = hideOutOfStockMessage && availability?.messageType === AvailabilityMessageType.OutOfStock;

    let showDash = true;
    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        if (hideAvailablity) {
            if (product) {
                const customOutOfStockMessage = product.properties?.customOutOfStockMessage
                    ? translate(product.properties?.customOutOfStockMessage)
                    : siteMessage("Werner_CustomOutOfStockMessage");
                inventoryTextComponent = (
                    <Notify
                        {...styles.customOutOfStockMessageStyle}
                        type="info"
                        data-test-selector="availabilityOutOfStockMessage"
                    >
                        {customOutOfStockMessage}
                    </Notify>
                );
            } else {
                inventoryTextComponent = <></>;
            }
            showDash = false;
        } else {
            inventoryTextComponent = (
                <InventoryMessage color={color} {...styles.inventoryMessage}>
                    {/* XNG-Changes: remove icon */}
                    {/* <Icon color={color} src={iconSrc} /> */}

                    <Tooltip
                        text={siteMessage("Werner_Inventory_CustomInventoryMsg")}
                        triggerComponent={
                            <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                                {parse(availability.message, parserOptions)}
                            </Typography>
                        }
                    />
                </InventoryMessage>
            );
        }
    } else if (availability) {
        inventoryTextComponent = <></>;
        showDash = false;
    }
    // <XNG-Changes>
    const renderAvailabilityMessage = (pickupMessage?: string, hideAvailability?: boolean) => {
        if (!pickupMessage) {
            return inventoryTextComponent;
        }
        if (hideAvailability) {
            return <></>;
        }
        return (
            <Typography {...styles.warehouseMessageText} data-test-selector="availabilityMessage">
                {pickupMessage}
            </Typography>
        );
    };
    // </XNG-Changes>
    // XNG-Changes: change structure to styledWrapper instead of GridContainer
    return (
        <>
            {isProductDetailsPage && pickupMessage && (
                <StyledWrapper {...styles.container} {...{ isProductDetailsPage }}>
                    {renderAvailabilityMessage("")}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper
                    {...styles.container}
                    {...{ isProductDetailsPage: isProductDetailsPage && !pickupMessage }}
                >
                    {/* <StyledWrapper {...styles.messageGridItem}> */}
                    <>
                        {isProductDetailsPage && renderAvailabilityMessage(pickupMessage, hideAvailablity)}
                        {!linkOnly &&
                            !isProductDetailsPage &&
                            renderAvailabilityMessage(pickupMessage, hideAvailablity)}
                    </>
                    {/* </GridItem> */}
                    {showLink(
                        availability,
                        trackInventory,
                        productSettings,
                        isProductDetailsPage,
                        // XNG-Change: hidelink --- WES-1889
                        hideLink || hideAvailablity,
                    ) && (
                        // <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                        <>
                            <StyledWrapper className="availability-link-wrapper">
                                {!linkOnly && showDash && <Typography {...styles.dash}>|</Typography>}
                                <Link
                                    {...styles.availabilityByWarehouseLink}
                                    onClick={viewAvailabilityByWarehouseClickHandler}
                                    data-test-selector="availabilityByWarehouseLink"
                                    {...{ linkOnly }}
                                    aria-label="Check Warehouses"
                                >
                                    {translate("Check Warehouses")}
                                </Link>
                            </StyledWrapper>
                            <StyledWrapper className="availability-link-wrapper">
                                {shouldShowViewSimilarProducts && (
                                    <>
                                        <Typography {...styles.dash}>|</Typography>
                                        <Link
                                            {...styles.availabilityByWarehouseLink}
                                            onClick={scrollToSimilarProducts}
                                            data-test-selector="viewSimilarProductsLink"
                                            aria-label="View Similar Products"
                                        >
                                            {translate("View Similar Products")}
                                        </Link>
                                    </>
                                )}
                            </StyledWrapper>
                        </>
                        // </GridItem>
                    )}
                </StyledWrapper>
                {showLink(availability, trackInventory, productSettings, isProductDetailsPage, hideLink) && (
                    <>
                        {(supplierModalOpen || modalIsOpen) && (
                            <>
                                <Modal
                                    {...styles.availabilityByWarehouseModal}
                                    headline={
                                        supplierModalOpen
                                            ? translate("Supplier Inventory")
                                            : translate("Warehouse Inventory")
                                    }
                                    isOpen={modalIsOpen || supplierModalOpen}
                                    handleClose={modalCloseHandler}
                                    execludeFromScrollingElements={
                                        !isLoading &&
                                        !errorMessage && (
                                            <>
                                                {!isSiteMessageEmpty("Werner_Warehouse_Inventory_Popup_Disclaimer") &&
                                                    !supplierModalOpen && (
                                                        <Notify
                                                            type="danger"
                                                            data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                                                        >
                                                            {siteMessage("Werner_Warehouse_Inventory_Popup_Disclaimer")}
                                                        </Notify>
                                                    )}
                                            </>
                                        )
                                    }
                                >
                                    {isLoading && (
                                        <StyledWrapper {...styles.centeringWrapper}>
                                            <LoadingSpinner {...styles.spinner} />
                                        </StyledWrapper>
                                    )}
                                    {!isLoading && (
                                        <>
                                            {errorMessage || supplierErrorMessage ? (
                                                <Notify type="danger" showClose={false}>
                                                    {supplierModalOpen ? supplierErrorMessage : errorMessage}
                                                </Notify>
                                            ) : (
                                                <>
                                                    <DataTable
                                                        {...styles.availabilityByWarehouseTable}
                                                        data-test-selector="availabilityByWarehouseModal"
                                                    >
                                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                            <DataTableHeader
                                                                {...styles.availabilityByWarehouseTableHeader}
                                                            >
                                                                {translate("Warehouse")}
                                                            </DataTableHeader>
                                                            <DataTableHeader
                                                                {...styles.availabilityByWarehouseTableHeader}
                                                            >
                                                                {translate("QTY")}
                                                            </DataTableHeader>
                                                        </DataTableHead>
                                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                            {(supplierModalOpen ? supplierWarehouses : warehouses)?.map(
                                                                (warehouse: any) => (
                                                                    <DataTableRow
                                                                        {...styles.availabilityByWarehouseTableRow}
                                                                        key={warehouse.name}
                                                                    >
                                                                        <DataTableCell
                                                                            {...styles.availabilityByWarehouseTableCell}
                                                                            data-test-selector="warehouseAvailabilityModal_Description"
                                                                        >
                                                                            {warehouse.description ||
                                                                                warehouse.warehouseName}
                                                                        </DataTableCell>
                                                                        <DataTableCell
                                                                            {...styles.availabilityByWarehouseTableCell}
                                                                            data-test-selector="warehouseAvailabilityModal_Quantity"
                                                                        >
                                                                            {warehouse.qty === 0 ? "-" : warehouse.qty}
                                                                        </DataTableCell>
                                                                    </DataTableRow>
                                                                ),
                                                            )}
                                                        </DataTableBody>
                                                    </DataTable>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Modal>
                            </>
                        )}
                        {hasSupplierInventory && isAuthenticated && (
                            <StyledWrapper {...styles.supplierInventory}>
                                <Link
                                    {...styles.availabilityByWarehouseLink}
                                    onClick={openSupplierAvailabilityModal}
                                    data-test-selector="rockwellInventory"
                                    {...{ linkOnly }}
                                    aria-label={siteMessage("Werner_Click_For_Brand_Inventory")
                                        .toString()
                                        .replace("{0}", product?.brand?.name || "")}
                                >
                                    {siteMessage("Werner_Click_For_Brand_Inventory")
                                        .toString()
                                        .replace("{0}", product?.brand?.name || "")}
                                </Link>
                            </StyledWrapper>
                        )}
                    </>
                )}
                {leadTimeMessage && (
                    <Notify {...styles.leadTimeTextStyle} type="info" data-test-selector="availabilityLeadTimeMessage">
                        {translate(leadTimeMessage)}
                    </Notify>
                )}
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
